/**
 * reef-header-guard.js — Reef Lagoon override-only mobile-freeze guard.
 *
 * Root cause (confirmed in diagnostic): on iOS a single tap can desync the
 * native <details open> state from the theme's JS toggle. `open` gets stranded
 * `true` while the drawer is visually closed, so dialog.js's document-level
 * `toggle` listener never runs its `false` branch, `unlockScroll` is missed, and
 * <html scroll-lock> sticks — freezing the whole page. Commit 3d3f6aa patched the
 * two *symptoms* (tap-eating backdrop + a JS-path unlock in header-drawer.js);
 * this guard is the belt-and-braces net for the residual desync itself.
 *
 * Strategy: whenever the drawer is provably NOT open (no `.menu-open`) but the
 * root scroll-lock is still set and nothing else legitimately holds a lock,
 * drive the <details> closed so the theme's OWN toggle -> unlockScroll path fires
 * (dialog.js listens with { capture: true }, so a programmatic `open = false`
 * reaches it and cleans utilities.js's scrollLockOwners Set the correct way).
 * Only if the attribute is somehow still set a frame later do we clear
 * <html scroll-lock> directly, as a last resort. We NEVER touch scrollLockOwners.
 *
 * Override-only: no base files edited, no imports (plain deferred script), guards
 * double-init, and is a harmless no-op on desktop (the drawer never opens there).
 */
(function () {
  if (window.__reefHeaderGuard) return;
  window.__reefHeaderGuard = true;

  var DETAILS_ID = 'Details-menu-drawer-container';
  // Long enough for a real open to add `.menu-open` on the next frame, so a
  // fresh open (open=true before the class lands) is never mistaken for a strand.
  var VERIFY_DELAY_MS = 150;

  function getDetails() {
    return document.getElementById(DETAILS_ID);
  }

  // The drawer is "really open" only when the panel-visibility class is present.
  function isReallyOpen(details) {
    return !!details && details.classList.contains('menu-open');
  }

  // Is some OTHER component (cart/search dialog, another scroll-lock drawer)
  // legitimately holding a scroll lock right now? If so, leave it alone.
  function otherLockHolderPresent(details) {
    var holders = document.querySelectorAll('details[scroll-lock][open], dialog[open]');
    for (var i = 0; i < holders.length; i++) {
      if (holders[i] !== details) return true;
    }
    return false;
  }

  function releaseIfStuck() {
    var details = getDetails();
    if (!details) return;

    var html = document.documentElement;
    if (!html.hasAttribute('scroll-lock')) return; // nothing is locked
    if (isReallyOpen(details)) return; // legitimately open — keep the lock
    if (otherLockHolderPresent(details)) return; // someone else owns the lock

    // Primary path: fire the theme's own unlock. Clearing `open` dispatches a
    // native `toggle`, which dialog.js turns into unlockScroll(details) and
    // prunes scrollLockOwners for us — no Set corruption.
    if (details.hasAttribute('open')) {
      details.classList.remove('menu-open');
      try {
        details.open = false;
      } catch (e) {
        /* no-op */
      }
      var summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    }

    // Last resort: if the lock is still set on the next frame (toggle never
    // fired, or the owner was already gone from the Set), clear the CSS-driven
    // root lock directly. base.css keys overflow:hidden off <html scroll-lock>,
    // so removing the attribute releases both <html> and .page-wrapper.
    requestAnimationFrame(function () {
      if (
        html.hasAttribute('scroll-lock') &&
        !isReallyOpen(details) &&
        !details.hasAttribute('open') &&
        !otherLockHolderPresent(details)
      ) {
        html.removeAttribute('scroll-lock');
        var pageWrapper = document.querySelector('.page-wrapper');
        if (pageWrapper && pageWrapper.style && pageWrapper.style.overflow) {
          pageWrapper.style.removeProperty('overflow');
        }
      }
    });
  }

  // Debounced + deferred so a burst of signals coalesces and a real open has a
  // frame to add `.menu-open` before we evaluate the "stuck" condition.
  var pending = null;
  function scheduleRelease() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      releaseIfStuck();
    }, VERIFY_DELAY_MS);
  }

  function wire(details) {
    if (!details) return false;

    // Signal 1 — native `toggle` on the root drawer (open, close, and strand).
    // toggle does not bubble, so this only fires for the root's own state.
    details.addEventListener('toggle', scheduleRelease);

    // Signal 2 — attribute changes: `open` stranded, or `.menu-open` removed on
    // a JS/back/Esc close path that didn't round-trip the native toggle.
    var mo = new MutationObserver(scheduleRelease);
    mo.observe(details, { attributes: true, attributeFilter: ['open', 'class'] });

    return true;
  }

  function init() {
    var details = getDetails();

    if (!wire(details)) {
      // Drawer not in the DOM yet — watch briefly for it, then wire and stop.
      var bodyObserver = new MutationObserver(function () {
        var d = getDetails();
        if (d && wire(d)) bodyObserver.disconnect();
      });
      bodyObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    // Signal 3 — iOS bfcache restore (back/forward) can return with a stale lock.
    window.addEventListener('pageshow', scheduleRelease);

    // Signal 4 — returning to the tab/app after backgrounding mid-desync.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') scheduleRelease();
    });

    // One-shot sweep for a lock stranded across a normal load.
    scheduleRelease();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
